import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RECEIPT_SAID_FOR_MS } from "@/components/courses/use-unit-draft";
import type { Read, Result, TaskBox } from "@/components/ui/box.schema";
import { attendanceApi } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { courseContentQueries } from "./content/use-course-content";

/**
 * Adds the sessions the course's meeting pattern implies over its term, where
 * they are missing. What it added is an event: said, then cleared after
 * `RECEIPT_SAID_FOR_MS`. A refusal stays until the next try.
 */
export function useGenerateClasses(courseId: string) {
  const queryClient = useQueryClient();
  const generate = useMutation({
    mutationFn: () => attendanceApi.generateSessions(courseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["course-workspace-sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["course-session-register"] });
      void queryClient.invalidateQueries({ queryKey: ["attendance", "course", courseId] });
      void queryClient.invalidateQueries({ queryKey: ["course-home"] });
      void queryClient.invalidateQueries(courseContentQueries(courseId));
      // Generation can renumber this course's sessions and gives them new
      // neighbours, which its session pages name. An open one reads again in
      // place; one shown earlier is dropped, so it never paints the old names.
      for (const [queryKey, workspace] of queryClient.getQueriesData<SessionWorkspace>({ queryKey: ["session-workspace"] })) {
        if (workspace?.session.course_id !== courseId) continue;
        queryClient.removeQueries({ queryKey, exact: true, type: "inactive" });
        void queryClient.invalidateQueries({ queryKey, exact: true, type: "active" });
      }
    },
  });
  const { isSuccess, reset } = generate;
  useEffect(() => {
    if (!isSuccess) return;
    const timer = window.setTimeout(reset, RECEIPT_SAID_FOR_MS);
    return () => window.clearTimeout(timer);
  }, [isSuccess, reset]);
  return generate;
}


/**
 * Generate sessions as the teaching team's Task beside All sessions (O1):
 * its result stays in the box, never an event that clears itself (W2).
 * Generating adds only what is missing, so a failed send is safe to repeat.
 */
export function useGenerateSessionsBox(generate: ReturnType<typeof useGenerateClasses>, read: Read<null>): TaskBox<null> {
  const [result, setResult] = useState<Result>({ state: "none" });
  return {
    kind: "task",
    id: "generate-sessions",
    job: "Add the sessions the course's meeting days imply over its term.",
    label: "Generate sessions",
    owner: { system: "imam" },
    // The course's meeting days are this box's read: it keeps its place while they load, or when there are none (P4).
    read,
    fold: "settled",
    state: () => (result.state === "confirmed" ? { class: "done", word: result.cap ?? "Generated" }
      : result.state === "refused" ? { class: "failed", word: "Not generated" }
        : { class: "quiet", word: "From the meeting days" }),
    detail: () => "Sessions come from the course's meeting days and term.",
    act: { verb: "Generate sessions", does: "open" },
    parts: [],
    commit: {
      verb: "Generate sessions",
      cap: "When you generate",
      effects: () => [
        { id: "adds", sentence: "Add each session the meeting days imply over the term, where it is missing" },
        { id: "numbers", sentence: "Number the course's sessions in date order" },
      ],
      blockers: () => [],
      run: async () => {
        let next: Result;
        try {
          const added = (await generate.mutateAsync()).length;
          next = { state: "confirmed", cap: added ? `${added} ${added === 1 ? "session" : "sessions"} added` : "No new sessions to add", at: new Date().toISOString(), effects: [], remains: [] };
        } catch (error) {
          next = { state: "refused", cap: "Not generated", sentence: error instanceof ApiError ? error.message : "Sessions could not be generated. Generating again adds only what is missing.", draft: "kept" };
        }
        setResult(next);
        return next;
      },
    },
    result,
    draft: false,
  };
}
